// Ported from dev-scrips-pm-react src/billing/components/management-discount-dialog.tsx (PROD-3312 appointment Billing
// tab, step 4): the invoice-wide MANAGEMENT discount (Self-pay), Flutter `AddManagementDiscountPopup`, opened from the
// Patient Payable / Management Discount row of the totals box. Adds via POST /Billing/ManagementDiscount/Create, lists
// via GET /Billing/ManagementDiscount/{invoiceId} (only while open) and removes via PUT /Billing/ManagementDiscount/Delete
// from the sibling "Remove this discount?" AlertDialog. Reasons are the fixed six; the 30% / patient-payable messages
// are advisory (management-discount-validation.ts).
// Differences from pm-react, none of them behavioural:
//   - the form is its own component, mounted only while the dialog is open, so every open starts from Amt / '' / ''
//     with nothing pending, which is what pm-react's open-gated reset effect produced.
//   - the reason list is the shared `DISCOUNT_REASONS`; the reason trigger gets an aria-label.
//   - classes: `bg-card` -> `bg-surface-card` (same token); `[&_input]:tabular-nums` (arbitrary variant, banned) ->
//     `tabular-nums` on the field.
import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { TextField } from '@scripsteam/scrips-signal-ds';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  BridgeButton as Button,
  BridgeSelect as Select,
  BridgeTooltipTrigger as TooltipTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ROW_ICON_BUTTON,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
} from '@/billing/bridge';
import { DISCOUNT_REASONS } from '@/billing/lib/discount-reasons';
import { validateDiscountAmount } from '@/billing/lib/management-discount-validation';
import {
  useAddManagementDiscount,
  useDeleteManagementDiscount,
  useManagementDiscounts,
} from '@/billing/services/use-invoice-actions';
import { cn } from '@/lib/utils';
import { formatMoney } from '@/shared/lib/money';
import type { ManagementDiscount } from '@/shared/types/billing';

function fmtDiscountDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface ManagementDiscountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  currency?: string;
  /** Patient payable on the invoice: a flat (Amt) discount above it draws the advisory message. */
  patientPayable?: number;
}

export function ManagementDiscountDialog({ open, ...rest }: ManagementDiscountDialogProps) {
  return open ? <ManagementDiscountForm {...rest} /> : null;
}

function ManagementDiscountForm({
  onOpenChange,
  invoiceId,
  currency = 'AED',
  patientPayable,
}: Omit<ManagementDiscountDialogProps, 'open'>) {
  const addDiscount = useAddManagementDiscount();
  const discountsQ = useManagementDiscounts(invoiceId, true);
  const deleteDiscount = useDeleteManagementDiscount(invoiceId);

  const [type, setType] = useState<'Amt' | 'Pct'>('Amt');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  // Which discount is pending removal: one controlled AlertDialog rendered as a SIBLING of the Dialog (not one per row
  // inside it), or the Dialog's focus trap swallows the AlertDialog portal's clicks.
  const [pendingDelete, setPendingDelete] = useState<ManagementDiscount | null>(null);

  const { parsed, isValid: validAmount, error: amountError } = validateDiscountAmount(type, amount, patientPayable);
  const hasReason = reason.trim() !== '';
  const canApply = validAmount && hasReason && Boolean(invoiceId);

  const apply = async () => {
    if (!canApply) return;
    try {
      await addDiscount.mutateAsync({ invoiceId, type, amount: parsed, reason });
      // Keep the dialog open so the new row appears in the table; clear the inputs.
      setAmount('');
      setReason('');
    } catch {
      /* Global toast surfaces failure. */
    }
  };

  // In-place reset: Flutter's cross icon clears amount / reason and returns the type to 'Amt' (PROD-1794 section A).
  const isPristine = amount === '' && reason === '' && type === 'Amt';
  const resetForm = () => {
    setType('Amt');
    setAmount('');
    setReason('');
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteDiscount.mutate(pendingDelete.id, { onSuccess: () => setPendingDelete(null) });
  };

  const existing = discountsQ.data ?? [];

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Management discount</DialogTitle>
            <DialogDescription>Apply an invoice-wide discount across eligible services.</DialogDescription>
          </DialogHeader>

          {/* Discounts already applied to this invoice (PROD-1751). */}
          {existing.length > 0 && (
            <div className="space-y-2 px-1">
              <label className="text-xs font-medium text-text-muted">Applied discounts</label>
              <div className="overflow-x-auto scrollbar-hide rounded-md border border-border-default">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-text-muted">
                      <th className="px-3 py-2 font-medium">Reason</th>
                      <th className="px-3 py-2 font-medium text-right">Amount</th>
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="w-10 px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {existing.map((d) => (
                      <tr key={d.id} className="border-t border-border-default">
                        <td className="px-3 py-2 text-text-primary">{d.reason || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {d.type === 'Pct' ? `${d.amount ?? 0}%` : `${currency} ${formatMoney(d.amount)}`}
                        </td>
                        <td className="px-3 py-2 text-text-muted">{fmtDiscountDate(d.createdOn)}</td>
                        <td className="px-3 py-2">
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <button
                                  type="button"
                                  aria-label="Remove discount"
                                  onClick={() => setPendingDelete(d)}
                                  disabled={deleteDiscount.isPending}
                                  className={`${ROW_ICON_BUTTON} text-status-error`}
                                />
                              }
                            >
                              <Trash2 className="size-4" aria-hidden />
                            </TooltipTrigger>
                            <TooltipContent>Remove discount</TooltipContent>
                          </Tooltip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="space-y-4 px-1">
            <div className="space-y-2">
              <label className="text-xs font-medium text-text-muted">Type</label>
              <div className="inline-flex rounded-md border border-border-default p-1 bg-grey-100 text-sm">
                {(['Amt', 'Pct'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={cn(
                      'px-3 py-1 rounded',
                      type === t
                        ? 'bg-surface-card text-brand-primary font-medium shadow-sm'
                        : 'text-text-muted hover:text-text-secondary',
                    )}
                  >
                    {t === 'Amt' ? `Amount (${currency})` : 'Percentage (%)'}
                  </button>
                ))}
              </div>
            </div>

            <TextField
              label={type === 'Pct' ? 'Discount %' : `Discount amount (${currency})`}
              type="text"
              size="compact"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={type === 'Pct' ? '0' : '0.00'}
              error={amountError}
              className="tabular-nums"
            />

            <div className="space-y-2">
              <label className="text-xs font-medium text-text-muted">Reason</label>
              <Select value={reason} onValueChange={(v) => setReason(v ?? '')}>
                <SelectTrigger className="w-full" aria-label="Reason">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {DISCOUNT_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {validAmount && !hasReason && <p className="text-xs text-status-error">Select a discount reason.</p>}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={addDiscount.isPending}>
              Close
            </Button>
            <Button variant="ghost" onClick={resetForm} disabled={isPristine || addDiscount.isPending}>
              Reset
            </Button>
            <Button onClick={apply} disabled={!canApply || addDiscount.isPending}>
              {addDiscount.isPending ? 'Applying…' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sibling of <Dialog> on purpose, see pendingDelete above. */}
      <AlertDialog
        open={pendingDelete != null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this discount?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the {pendingDelete?.reason || 'management'} discount from the invoice and recalculates
              Patient Payable. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteDiscount.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteDiscount.isPending}
              className="bg-status-error text-white hover:opacity-90"
            >
              {deleteDiscount.isPending ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
